import React, { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { Button, Row, Col, Container, Form } from "react-bootstrap";
import { useDispatch } from "react-redux";
import LeadForm from "./LeadForm";
import { validateLeadForm } from "../../utils/validation";
import { updateLeadThunk } from "../../features/Lead/leadsSlice";
import useCustomTranslation from "../../hooks/useCustomTranslation";
import {
  formatDate,
  isValidNameWithSpecialChars,
  isValidNumberInput,
} from "../../utils/Utils";
import { fetchCountries } from "../../redux/Slices/locationSlice";
import { useSelector } from "react-redux";
import { Avatar } from "antd";
import { setToastForAddLead } from "../../features/Lead/leadsSlice";
import Loader from "../../components/Common/Loader";
import { getOfferDetails } from "../../redux/Slices/sentOfferSlice";

const EditLead = () => {
  const { state: lead } = useLocation();
  const { trans } = useCustomTranslation();
  const { profileData } = useSelector((state) => state.profile);
  const { countries } = useSelector((state) => state.location);
  const { loading } = useSelector((state) => state.leads);
  const isReadOnly = lead?.isReadOnly || false;
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [leadData, setLeadData] = useState({
    leadId: lead?.key || "",
    firstName: lead?.name?.trim().split(" ")[0]?.trim() || "",
    lastName: lead?.name?.trim().split(" ").slice(1).join(" ")?.trim() || "",
    name: lead?.name?.trim() || "",
    email: lead?.email || "",
    phoneNumber: lead?.phone || "",
    language: lead?.language || "",
    note: lead?.note || "",
    status: lead?.status || "New Lead",
    createdOn: lead?.createdOn || "",
    isLeadFromSite: lead?.isLeadFromSite || false,
    coachId: profileData?.coachId || "",
    id: lead?.id || null,
    country: lead?.country || "",
    goal: lead?.goal || "",
    sex: lead?.sex || "",
    age: lead?.age || 0,
    motivation: lead?.motivation || "",
    sixMonthFitnessGoal: lead?.sixMonthFitnessGoal || "",
    isPreparedToInvest: lead?.isPreparedToInvest || "",
    timeZone: lead?.timeZone || "",
  });

  const [offerData, setOfferData] = useState({});
  const [oneTimePayment, setoneTimePayment] = useState([]);
  const [isFreeClient, setIsFreeClient] = useState(true)

  const [errors, setErrors] = useState({});

  // Fetch countries on mount
  useEffect(() => {
    dispatch(fetchCountries());
  }, [dispatch]);

  useEffect(() => {
    const sendOffer = async () => {
      try {
        const offerResponse = await dispatch(
          getOfferDetails({ leadOfferDetailsId: lead?.key })
        ).unwrap();
        // eslint-disable-next-line react-hooks/exhaustive-deps

        setOfferData(offerResponse?.leadOfferDetails);

        if (offerResponse?.oneTimePayments != null) {
          // let totalOneTimePayment = 0;
          // for (let i = 0; i < offerResponse?.oneTimePayments.length; i++) {
          //   totalOneTimePayment =
          //   totalOneTimePayment +
          //   offerResponse?.oneTimePayments[i].paymentAmount;
          // }
          setoneTimePayment(offerResponse?.oneTimePayments || []);
          setIsFreeClient(offerResponse?.isFreeClient)
        }
      } catch (err) {
        console.error("Failed to send offer:", err);
      }
    };

    lead?.key && sendOffer();
  }, [dispatch, lead?.key]);

  const countriesArr = countries.map((country) => ({
    value: country.countryId,
    label: country.countryName,
    countryId: country.countryId,
  }));

  const handleChange = (e) => {
    const { name, value } = e.target;

    if (name === "phoneNumber") {
      if (!isValidNumberInput(value)) return;
    }

    if (name === "firstName" || name === "lastName") {
      if (!isValidNameWithSpecialChars(value)) return;
    }

    if (name === "note") {
      if (value.length > 255) {
        return;
      }
    }
    if (name === "country") {
      const selectedCountry = countriesArr.find(
        (country) => String(country.countryId) === String(value)
      );

      setLeadData((prev) => ({
        ...prev,
        country: selectedCountry ? selectedCountry.countryId : value,
      }));

      setErrors((prev) => ({ ...prev, country: "" }));

      return;
    }

    setLeadData((prev) => ({
      ...prev,
      [name]: value,
    }));
    setErrors((prev) => ({ ...prev, [name]: "" }));
  };

  const handleStatusChange = (newStatus) => {
    if (newStatus === "Won" || newStatus === "Offer Sent") return;
    setLeadData((prev) => ({
      ...prev,
      status: newStatus,
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    const { isValid, errors: validationErrors } = validateLeadForm(
      leadData,
      trans
    );

    if (isValid) {
      const payload = {
        leadId: leadData.leadId,
        ...leadData,
        createdBy: leadData.createdBy || "",
        coachId: profileData?.coachId || "",
        updatedBy: "",
        country: String(leadData.country) || "",
      };

      try {
        await dispatch(updateLeadThunk(payload)).unwrap();

        dispatch(
          setToastForAddLead({
            type: "success",
            message: trans("LeadPage.leadUpdatedSuccessfully"),
            heading: trans("Common.success"),
          })
        );

        navigate("/leads");
      } catch (error) {
        dispatch(
          setToastForAddLead({
            type: "error",
            message: trans("LeadPage.leadUpdateFailed"),
            heading: trans("Common.failed"),
          })
        );
      }
    } else {
      setErrors(validationErrors);
    }
  };

  const timelineStatuses = [
    { value: "New Lead", label: "New Lead" },
    { value: "Contacted", label: "Contacted" },
    { value: "Offer Sent", label: "Offer Sent" },
    { value: "Won", label: "Won" },
    { value: "Lost", label: "Lost" },
    { value: "Invalid", label: "Invalid" },
  ];

  const handleSendOffer = async () => {
    const { isValid, errors: validationErrors } = validateLeadForm(
      leadData,
      trans
    );
    const formattedOneTimeFees = oneTimePayment.map((item) => ({
      fee: Number(item.paymentAmount),
      billingDate: item.paymentDate,
      isReactivate: false,
    }));

    if (isValid) {
      try {
        const offerPayload = {
          leadId: leadData.leadId,
          firstName: leadData.firstName,
          lastName: leadData.lastName,
          email: leadData.email,
          country: String(leadData?.country) || "",
          phoneNumber: leadData.phoneNumber,
          language: leadData.language,
          note: leadData.note,
          isLeadFromSite: leadData.isLeadFromSite,
          createdOn: leadData.createdOn,
          status: leadData.status,
          subscriptionDuration: offerData.subscriptionDuration || "",
          monthlyAmount: offerData.monthlyAmount || "",
          setupFee: offerData.setupFee || "",
          billingCycle: offerData.billingCycle || "monthApart",
          freePeriodEndDate: offerData.freePeriodEndDate || null,
          currency: offerData.currency || "USD",
          leadNotes: offerData.leadNotes || "",
          customPaymentDate: offerData.customPaymentDate || 1,
          oneTimeFees: formattedOneTimeFees || [],
          billingStartDate: offerData.billingStartDate,
          isFreeClient: isFreeClient
        };

        navigate(`/leads/addNewOffer/${leadData.leadId}`, {
          state: offerPayload,
        });
      } catch (error) {
        console.error("Error sending offer:", error);
      }
    } else {
      setErrors(validationErrors);
    }
  };

  const capitalizeFirstWord = (text) => {
    if (!text) return "-";
    return text.charAt(0).toUpperCase() + text.slice(1).toLowerCase();
  };

  if (loading) {
    return <Loader />;
  }

  return (
    <div className="edit-lead-page">
      {leadData && (
        <>
          {/* Header Section */}
          <div className="section-container header-section d-flex justify-content-between">
            <div className="d-flex align-items-center">
              <Button
                variant="outline-primary"
                className="cancel-button"
                onClick={() => navigate("/leads")}
              >
                <i className="bi bi-chevron-left"></i> Back
              </Button>
              <h5 className="ms-3 fw-bold">{trans("LeadPage.editDetails")}</h5>
            </div>
            {!isReadOnly && (
              <div className="d-flex justify-content-between align-items-center">
                <Button
                  className="create-button me-2"
                  onClick={handleSendOffer}
                >
                  <i className="bi bi-send"></i> Send Offer
                </Button>
                <Button className="saveButton" onClick={handleSubmit}>
                  <i className="bi bi-save"></i> Save
                </Button>
              </div>
            )}
          </div>

          <div className="section-container profile-timeline-section">
            <Container fluid>
              <Row>
                {/* Profile Section */}
                <Col md={3} className="profile-section">
                  <div className="d-flex align-items-center">
                    <Avatar
                      src={lead?.profileImage || null}
                      size={52}
                      className="me-3 avatar-style"
                      alt="Lead profile"
                    >
                      {leadData.firstName?.[0] || "?"}
                    </Avatar>
                    <div>
                      <h5>{leadData.name}</h5>
                      {leadData.isLeadFromSite && (
                        <p className="text-muted">
                          {`${leadData.age || "-"} | ${capitalizeFirstWord(
                            leadData.sex
                          )} | ${capitalizeFirstWord(leadData.goal)}`}
                        </p>
                      )}
                    </div>
                  </div>
                </Col>

                {/* Timeline Section */}
                <Col md={9} className="timeline-section">
                  <div className="timeline-statuses">
                    {timelineStatuses.map((status, index) => (
                      <React.Fragment key={index}>
                        <div
                          className={`timeline-oval ${
                            leadData.status.toLowerCase() ===
                            status.value.toLowerCase()
                              ? "highlighted"
                              : ""
                          } ${
                            status.value === "Won" ||
                            status.value === "Offer Sent" ||
                            isReadOnly
                              ? "disabled-status"
                              : ""
                          }`}
                          onClick={() => {
                            if (
                              isReadOnly ||
                              (leadData.status === "Offer sent" &&
                                status.value === "New Lead")
                            ) {
                              return;
                            }
                            handleStatusChange(status.value);
                          }}
                          style={{
                            cursor:
                              isReadOnly ||
                              status.value === "Won" ||
                              status.value === "Offer Sent"
                                ? "not-allowed"
                                : "pointer",
                          }}
                        >
                          {status.label}
                        </div>
                        {index < timelineStatuses.length - 1 && (
                          <div className="timeline-line" />
                        )}
                      </React.Fragment>
                    ))}
                  </div>
                </Col>
              </Row>
            </Container>
          </div>

          {/* Form Sections */}
          <div className="section-container form-section">
            <Row className="d-flex justify-content-between align-items-center fw-bold text-primary mb-4">
              <Col>
                <h6 className="pb-2">{trans("Common.details")}</h6>
              </Col>
              {isReadOnly && (
                <Col xs="auto">
                  <Form.Check
                    type="checkbox"
                    label="Free Client"
                    name="isFreeClient"
                    checked={isFreeClient ?? false}
                    onChange={handleChange}
                  />
                </Col>
              )}
            </Row>
            <LeadForm
              formData={{
                ...leadData,
                createdOn: formatDate(leadData.createdOn),
              }}
              errors={errors}
              onChange={handleChange}
              isEditMode={!isReadOnly}
              showLeadInfo={true}
              countryOptions={countriesArr}
              languageOptions={[
                { value: "", label: "Select Language" },
                { value: "English", label: "English" },
                { value: "Spanish", label: "Spanish" },
                { value: "French", label: "French" },
              ]}
            />

            {/* Horizontal Line */}
            {leadData.isLeadFromSite && <hr className="my-4" />}

            {/* Lead Information Section */}
            {leadData.isLeadFromSite && (
              <div className="">
                <h5 className="fw-bold text-primary mb-4">
                  {trans("LeadPage.leadInformation")}
                </h5>
                <Container fluid>
                  <Row>
                    <Col md={2}>
                      <p className="fw-bold mb-1">{trans("LeadPage.goal")}</p>
                      <p className="text-muted">
                        {capitalizeFirstWord(leadData.goal)}
                      </p>
                    </Col>
                    <Col md={2}>
                      <p className="fw-bold mb-1">{trans("LeadPage.sex")}</p>
                      <p className="text-muted">
                        {capitalizeFirstWord(leadData.sex)}
                      </p>
                    </Col>
                    <Col md={2}>
                      <p className="fw-bold mb-1">{trans("LeadPage.age")}</p>
                      <p className="text-muted">{leadData.age || "-"}</p>
                    </Col>
                    <Col md={3}>
                      <p className="fw-bold mb-1">
                        {trans("LeadPage.motivation")}
                      </p>
                      <p className="text-muted">
                        {capitalizeFirstWord(leadData.motivation)}
                      </p>
                    </Col>
                    <Col md={2}>
                      <p className="fw-bold mb-1">
                        {trans("LeadPage.sixMonthGoal")}
                      </p>
                      <p className="text-muted">
                        {capitalizeFirstWord(leadData.sixMonthFitnessGoal)}
                      </p>
                    </Col>
                    <Col md={2}>
                      <p className="fw-bold mb-1">{trans("LeadPage.invest")}</p>
                      <p className="text-muted">
                        {capitalizeFirstWord(leadData.isPreparedToInvest)}
                      </p>
                    </Col>
                    <Col md={1}>
                      <p className="fw-bold mb-1">
                        {trans("LeadPage.timezone")}
                      </p>
                      <p className="text-muted">
                        {leadData.timeZone?.toUpperCase() || "-"}
                      </p>
                    </Col>
                  </Row>
                </Container>
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
};

export default EditLead;
